import React, { useMemo } from 'react';
import { Table, Button } from 'react-bootstrap';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

interface OccupancyTableYearProps {
    pivotedData: { [roomTypeId: string]: { [month: string]: string } };
    totalData: Array<{ month: string; occupancy: string; }>;
    yearlyDataDetailed: Array<{ year: number; room_type_id: string | null; occupancy: string; }>;
    yearlyDataTotal: Array<{ year: number; room_type_id: string | null; occupancy: string; }>;
    roomNamesMap: { [id: string]: string };
}

const MONTHS_NUMERIC = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];
const MONTH_NAMES = ["JANUARY", "FEBRUARY", "MARCH", "APRIL", "MAY", "JUNE", "JULY", "AUGUST", "SEPTEMBER", "OCTOBER", "NOVEMBER", "DECEMBER"];
const COLUMNS = ["Rooms", "Report", ...MONTH_NAMES, "Total"];

const OccupancyTableYear: React.FC<OccupancyTableYearProps> = ({
    pivotedData,
    totalData,
    yearlyDataDetailed,
    yearlyDataTotal,
    roomNamesMap
}) => {
    const formatOccupancy = (value?: string | null) => {
        if (value && parseFloat(value) > 0) {
            return `${value}%`;
        }
        return '-';
    };

    const totalMonthlyMap = useMemo(() => new Map(
        totalData.map(item => [item.month.split('-')[1], item.occupancy])
    ), [totalData]);

    const grandTotalYearly = yearlyDataTotal[0]?.occupancy || '-';

    const exportToPDF = () => {
        const doc = new jsPDF({ orientation: 'landscape' });
        doc.text("Occupancy Report - Yearly", 14, 10);

        const body = Object.keys(pivotedData).map(roomTypeId => {
            const roomName = roomNamesMap[roomTypeId] || roomTypeId;
            const monthlyOccupancies = MONTHS_NUMERIC.map(month => formatOccupancy(pivotedData[roomTypeId][month]));
            const yearlyTotal = formatOccupancy(yearlyDataDetailed.find(item => item.room_type_id === roomTypeId)?.occupancy);
            return [roomName, "Occupancy", ...monthlyOccupancies, yearlyTotal];
        });

        const foot = [[
            "Rooms", "Occupancy",
            ...MONTHS_NUMERIC.map(month => formatOccupancy(totalMonthlyMap.get(month))),
            formatOccupancy(grandTotalYearly)
        ]];

        autoTable(doc, { head: [COLUMNS], body, foot });
        doc.save("OccupancyReport_Yearly.pdf");
    };

    const exportToExcel = () => {
        const bodyData = Object.keys(pivotedData).map(roomTypeId => {
            const roomName = roomNamesMap[roomTypeId] || roomTypeId;
            const monthlyOccupancies = MONTHS_NUMERIC.map(month => formatOccupancy(pivotedData[roomTypeId][month]));
            const yearlyTotal = formatOccupancy(yearlyDataDetailed.find(item => item.room_type_id === roomTypeId)?.occupancy);
            return [roomName, "Occupancy", ...monthlyOccupancies, yearlyTotal];
        });

        const footerData = ["Rooms", "Occupancy", ...MONTHS_NUMERIC.map(month => formatOccupancy(totalMonthlyMap.get(month))), formatOccupancy(grandTotalYearly)];

        const wsData = [COLUMNS, ...bodyData, [footerData]];
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Yearly Report");
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(blob, "OccupancyReport_Yearly.xlsx");
    };

    return (
        <div className="mt-4">
            <div className="mb-3 d-flex gap-2">
                <Button variant="primary" onClick={exportToPDF}>Export to PDF</Button>
                <Button variant="success" onClick={exportToExcel}>Export to Excel</Button>
            </div>

            <div className="table-responsive">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            {COLUMNS.map((col) => <th key={col}>{col}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {Object.keys(pivotedData).length > 0 ? (
                            Object.keys(pivotedData).map(roomTypeId => {
                                const yearlyTotal = yearlyDataDetailed.find(item => item.room_type_id === roomTypeId)?.occupancy;
                                return (
                                    <tr key={roomTypeId}>
                                        <td>{roomNamesMap[roomTypeId] || roomTypeId}</td>
                                        <td>Occupancy</td>
                                        {MONTHS_NUMERIC.map(month => (
                                            <td key={month}>{formatOccupancy(pivotedData[roomTypeId][month])}</td>
                                        ))}
                                        <td>{formatOccupancy(yearlyTotal)}</td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={COLUMNS.length} className="text-center">
                                    No data available
                                </td>
                            </tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td><strong>Rooms</strong></td>
                            <td><strong>Occupancy</strong></td>
                            {MONTHS_NUMERIC.map(month => (
                                <td key={month}><strong>{formatOccupancy(totalMonthlyMap.get(month))}</strong></td>
                            ))}
                            <td><strong>{formatOccupancy(grandTotalYearly)}</strong></td>
                        </tr>
                    </tfoot>
                </Table>
            </div>
        </div>
    );
};

export default OccupancyTableYear;